import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableCellsIcon } from '@heroicons/react/24/outline';
import backend from '../../services/backend';
import { Badge } from '../../catalyst/badge';
import { Button } from '../../catalyst/button';
import { Link } from '../../catalyst/link';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { SortableTable, type SortableTableColumn } from '../../components/SortableTable';
import { PagePreviewHoverProvider } from '../../components/pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from '../../components/pagePreview/PagePreviewHoverCard';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useSessionStorageState } from '../../hooks/useSessionStorageState';
import { useAuth } from '../../hooks/useAuth';
import type { CurationQueueSource } from '@avisoma/shared';
import type { CurationItem } from './useCurationSession';
import { CURATION_SOURCE_LABELS } from '../../lib/curationSourceLabels';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { CurationFilters } from './CurationFilters';

const SOURCE_COLOR: Record<CurationQueueSource, 'rose' | 'amber' | 'sky' | 'zinc'> = {
  manual_flag: 'rose',
  ai_scanner: 'zinc',
  completeness: 'amber',
  user_mismatch: 'amber',
  second_opinion: 'amber',
  outlier: 'sky',
  low_certainty: 'zinc',
  cluster_page: 'sky',
  page_edit: 'zinc',
};

const QUEUE_LIMIT = 200;

export default function CurationTable() {
  useDocumentTitle('Curation list');

  // Persisted in sessionStorage so the chosen source/category survive a reload
  // or navigating away and back within the tab session.
  const [persistedFilters, setPersistedFilters] = useSessionStorageState('curation-list-filters', {
    source: '',
    category: '',
  });
  const sourceFilter = persistedFilters.source;
  const categoryFilter = persistedFilters.category;
  const setSourceFilter = (value: string) => setPersistedFilters((f) => ({ ...f, source: value }));
  const setCategoryFilter = (value: string) => setPersistedFilters((f) => ({ ...f, category: value }));

  const filters = { source: sourceFilter || undefined, category: categoryFilter || undefined };
  const { user } = useAuth();

  // Key includes the viewer's id + role: the queue is user-scoped (second_opinion
  // set + priorReviews name visibility), so cached rows must not leak across
  // users/emulated roles. Mirrors the leaderboard's key.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['curation', 'queue', 'table', user?.id, user?.role, filters],
    queryFn: async () => (await backend.getCurationQueue({ ...filters, limit: QUEUE_LIMIT })).data,
  });

  const total = data?.total ?? 0;
  const loaded = data?.items.length ?? 0;
  const isCapped = total > loaded;

  // Use the same param names the focus page (Curation.tsx) reads on mount. Carry
  // both params even when empty so the hand-off is authoritative — an empty value
  // means "all", overriding whatever the focus page last persisted.
  const focusModeQuery = new URLSearchParams({ source: sourceFilter, category: categoryFilter }).toString();

  const headerRight = (
    <div className="flex items-center gap-4 shrink-0">
      <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">{isLoading ? '…' : `${total} flagged`}</span>
      <Button href={focusModeQuery ? `/curation?${focusModeQuery}` : '/curation'} color="indigo">
        Start review
      </Button>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Curation list"
        description="Browse the review queue as a table"
        icon={<TableCellsIcon className="size-6" />}
      >
        {headerRight}
      </PageHeader>

      {/* Filter row — shared with the curation history view */}
      <CurationFilters
        source={sourceFilter}
        category={categoryFilter}
        onSourceChange={setSourceFilter}
        onCategoryChange={setCategoryFilter}
      />

      {/* Cap note — the queue request is limited; surface the omitted overflow */}
      {!isLoading && !isError && isCapped && (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Showing first {loaded} of {total} — narrow with filters.
        </p>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          <Skeleton variant="rect" className="h-12 w-full" />
          <Skeleton lines={6} />
        </div>
      )}

      {/* Error */}
      {!isLoading && isError && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-8 text-center space-y-4">
          <p className="text-red-600 dark:text-red-400 text-sm">Failed to load curation queue.</p>
          <Button outline onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && (data?.items.length ?? 0) === 0 && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
          Nothing flagged for review.
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (data?.items.length ?? 0) > 0 && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden">
          <PagePreviewHoverProvider visibleIds={new Set((data?.items ?? []).map((i) => i.pageId))}>
            <SortableTable
              data={data?.items ?? []}
              columns={columns}
              rowKey={(row) => row.pageId}
              storageId="curation-queue"
              defaultSortBy="currentCategory"
              limitHeight={false}
            />
          </PagePreviewHoverProvider>
        </div>
      )}
    </div>
  );
}

const columns: SortableTableColumn<CurationItem>[] = [
  {
    label: 'Preview',
    accessor: 'preview',
    render: (row) => <PreviewThumb pageId={row.pageId} />,
  },
  {
    label: 'Current',
    accessor: 'currentCategory',
    sortable: true,
    render: (row) =>
      row.currentCategory ? (
        <span className="font-medium text-gray-900 dark:text-zinc-100">{row.currentCategory}</span>
      ) : (
        <span className="italic text-gray-400 dark:text-zinc-500">Uncategorized</span>
      ),
  },
  {
    label: 'Suggested',
    accessor: 'suggestedCategory',
    sortable: true,
    render: (row) => (
      <span className="text-gray-700 dark:text-zinc-300">{row.suggestedCategory ?? '—'}</span>
    ),
  },
  {
    label: 'Source',
    accessor: 'source',
    sortable: true,
    render: (row) => (
      <Badge color={SOURCE_COLOR[row.source]}>{CURATION_SOURCE_LABELS[row.source]}</Badge>
    ),
  },
  {
    label: 'Signal',
    accessor: 'signal',
    render: (row) => <SignalCell item={row} />,
  },
  {
    label: 'Links',
    accessor: 'links',
    render: (row) => (
      <div className="flex items-center gap-3 whitespace-nowrap">
        <Link
          href={`/document-pages/${row.pageId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          View page ↗
        </Link>
        {row.documentId && (
          <Link
            href={`/documents/${row.documentId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Document ↗
          </Link>
        )}
      </div>
    ),
  },
];

function PreviewThumb({ pageId }: { pageId: string }) {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <div className="flex items-center justify-center h-12 w-9 rounded bg-gray-100 dark:bg-zinc-700 text-[10px] text-gray-400 dark:text-zinc-500">
        n/a
      </div>
    );
  }
  return (
    <PagePreviewHoverCard page={{ id: pageId }} className="inline-block">
      <img
        src={pagePreviewSrc(pageId, { as: 'png' })}
        alt="Page preview"
        loading="lazy"
        className="h-12 w-auto rounded border border-gray-200 dark:border-zinc-700 object-contain"
        onError={() => setImgError(true)}
      />
    </PagePreviewHoverCard>
  );
}

function SignalCell({ item }: { item: CurationItem }) {
  const { source, signal, suggestedCategory } = item;

  if (source === 'outlier') {
    return (
      <span className="text-gray-500 dark:text-zinc-400">
        {signal.rivalScore != null && signal.ownScore != null
          ? `${signal.rivalScore.toFixed(2)} vs ${signal.ownScore.toFixed(2)}`
          : '—'}
      </span>
    );
  }

  if (source === 'low_certainty') {
    return (
      <span className="text-gray-500 dark:text-zinc-400">
        {signal.ownScore != null ? `Low certainty (${signal.ownScore.toFixed(2)})` : 'Low certainty'}
      </span>
    );
  }

  if (source === 'user_mismatch') {
    return (
      <span className="text-gray-500 dark:text-zinc-400">
        {suggestedCategory ? `→ '${suggestedCategory}'` : '—'}
      </span>
    );
  }

  if (source === 'second_opinion') {
    return (
      <span className="text-gray-500 dark:text-zinc-400">
        {suggestedCategory ? `2nd opinion · classifier → '${suggestedCategory}'` : '2nd opinion'}
      </span>
    );
  }

  if (source === 'ai_scanner') {
    // AI-scanner flags carry the verdict (suggested category + confidence + reasoning) in
    // `signal.note`; the queue leaves top-level `suggestedCategory` null for all flag sources,
    // so render the self-describing note rather than a never-populated `→ 'category'` line.
    return (
      <span className="text-gray-500 dark:text-zinc-400">{signal.note ?? 'AI flagged'}</span>
    );
  }

  if (source === 'completeness') {
    // The note lists the empty required fields; the flagger is the detector's sentinel, not a person.
    return (
      <span className="text-gray-500 dark:text-zinc-400">{signal.note ?? 'Incomplete extraction'}</span>
    );
  }

  // manual_flag
  return (
    <span className="text-gray-500 dark:text-zinc-400">
      {signal.flaggedBy ?? 'unknown'}
      {signal.note && <span className="text-gray-400 dark:text-zinc-500"> — {signal.note}</span>}
    </span>
  );
}
